import {
  Component,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { PAGE } from 'src/app/routes';
import { GameScoreService } from 'src/app/core/services/game-score/game-score.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { TranslocoService } from '@jsverse/transloco';
import { GameId } from 'src/app/model/game/game-id';
import { ModalController, Platform } from '@ionic/angular';
import { PlayerStatsDrawerComponent } from '../results/player-stats-drawer/player-stats-drawer.component';
import { gameIdToName } from 'src/app/model/game/game-id.utils';
import { GameInterfaceState } from '../game/controllers/listeners/ux-game-listener';

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeaderComponent implements OnInit {
  @Output() closeClicked = new EventEmitter<void>();
  @Output() themeClicked = new EventEmitter<void>();
  @Output() tabSelected = new EventEmitter<string>();

  private router = inject(Router);
  private modalController = inject(ModalController);
  private platform = inject(Platform);

  operatorService = inject(OperatorService);
  translocoService = inject(TranslocoService);
  themeService = inject(ThemeService);
  gameScoreService = inject(GameScoreService);

  private readonly currentUrl = signal<string>(this.router.url);

  showGameScore = computed(() => {
    const url = this.currentUrl();
    const gameState = this.gameScoreService.gameInterfaceState();

    const onGameRoute = url.startsWith(`/${PAGE.GAME}`);

    const isGameActive = gameState === GameInterfaceState.GameStarted;

    return onGameRoute && isGameActive;
  });

  showNavbar = computed(() => {
    const url = this.currentUrl();
    const gameState = this.gameScoreService.gameInterfaceState();

    const onGameRoute = url.startsWith(`/${PAGE.GAME}`);
    if (onGameRoute) {
      return gameState !== GameInterfaceState.GameStarted;
    }

    const hideNavbarRoutes = ['/'];

    const hide =
      hideNavbarRoutes.includes(url) || url.startsWith('/?') || url.startsWith('/?redirect=true');

    return !hide;
  });

  GameId = GameId;
  OperatorService = OperatorService;

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects); // Use `urlAfterRedirects` for more stability
      }
    });
  }

  get currencyIcon(): string | undefined {
    return this.themeService.currencyIcon();
  }

  get balance(): string | undefined {
    return this.operatorService.formattedBalance() ?? undefined;
  }

  get isDemo(): boolean {
    return OperatorService.IsDemo;
  }

  get tabs(): { label: string; id: string }[] {
    return [
      {
        label: this.translocoService.translate('tabs.play'),
        id: `${PAGE.GAME}/${this.operatorService.gameId}`,
      },
      { label: this.translocoService.translate('tabs.results'), id: PAGE.RESULTS },
      { label: this.translocoService.translate('tabs.leaderboard'), id: PAGE.CHOOSE_LEADERBOARDS },
      {
        label: this.translocoService.translate('tabs.rules'),
        id: `${PAGE.SELECT_RULES}/${this.operatorService.gameId}`,
      },
    ];
  }

  get gameId(): GameId {
    return this.operatorService.gameId as GameId;
  }

  get gameName(): string {
    return gameIdToName(this.operatorService.gameId as GameId, this.translocoService);
  }

  onCloseClicked() {
    this.closeClicked.emit();
  }

  onThemeClicked() {
    this.themeClicked.emit();
  }

  onTabSelected(tabId: string) {
    this.tabSelected.emit(tabId);
  }

  onBackClicked() {
    window.location.href = '?redirect=true';
  }

  onOpponentClicked(opponentId: string) {
    this.openModal(opponentId);
  }

  private async openModal(userId: string) {
    const isMobile = this.platform.is('mobile');
    let modalWillDismiss: () => void;

    const modal = await this.modalController.create({
      component: PlayerStatsDrawerComponent,
      componentProps: {
        userId,
        gameId: this.operatorService.gameId,
        modalWillDismissCallback: (callback: () => void) => {
          modalWillDismiss = callback;
        },
      },
      initialBreakpoint: isMobile ? 1 : undefined,
      breakpoints: isMobile ? [0, 1] : undefined,
      handle: false,
      backdropDismiss: false,
      canDismiss: async (_, role) => role !== 'gesture',
      cssClass: isMobile ? 'mobile-rounded-modal' : 'desktop-rounded-modal',
    });

    modal.onWillDismiss().then(() => {
      modalWillDismiss?.();
    });

    await modal.present();
  }
}
